import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {componentEnterLeaveAnimation} from '@application/animations/component-enter-leave.animation';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {MachineMessagesGroup} from '@domain/machine/machine-messages-group.interface';
import {MachineType} from '@domain/machine/machine-type.enum';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {IPageInfo} from '@iharbeck/ngx-virtual-scroller';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {MachineDetails} from '@infrastructure/http/planning/machine-details';
import {Planning, PLANNING} from '@infrastructure/http/planning/planning';
import {AssertionUtils, BaseComponent, Message} from '@vdw/angular-component-library';
import {concat, each, first, isEmpty, size, slice} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';
import {PlanningSidebarData} from '../../planning-sidebar-data.interface';
import {PlanningSidebarType} from '../planning-sidebar-type.enum';

@Component({
  selector: 'app-machine-detail-sidebar',
  templateUrl: './machine-detail-sidebar.component.html',
  styleUrls: ['./machine-detail-sidebar.component.scss'],
  animations: [componentEnterLeaveAnimation()]
})
export class MachineDetailSidebarComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public machine: PlanningMachine;
  @Output() public machineDetailSidebarClose: EventEmitter<void> = new EventEmitter<void>();
  public messagesGroups: MachineMessagesGroup[];
  private messages: Message[];
  private readonly SLICE_LENGTH = 20;
  private hasMoreMessages = true;
  private currentSubscription: Subscription;

  public constructor(
    @Inject(PLANNING) private readonly planning: Planning,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly router: Router,
    private readonly navigationHelperService: NavigationHelperService<PlanningSidebarData>
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
  }

  public ngOnChanges(): void {
    if (!AssertionUtils.isNullOrUndefined(this.machine)) {
      this.getMachineDetails();
      if (this.hasMessages()) {
        this.getMessages(0);
        this.groupMessagesByChronologicalOrderAndType();
      }
    }
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<PlanningSidebarData>({
      sidebarType: PlanningSidebarType.MACHINE,
      sidebarPlanningMachine: this.machine
    });
  }

  public closeMachineDetail(): void {
    this.machineDetailSidebarClose.emit();
  }

  public getIconNameForMachineType(): string {
    return MachineType.getIconNameForMachineType(this.machine.machineType);
  }

  public isMachineDefined(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.machine);
  }

  public getMachineTypeLabelForTranslate(): string {
    return `MACHINE.TYPES.${MachineType[this.machine.machineType]}`;
  }

  public isMachineOfTypeCarpetLoom(): boolean {
    return [MachineType.CARPET_LOOM, MachineType.CARPET_LOOM_NO_JQ, MachineType.VELVET, MachineType.VELVET_NO_JQ].includes(this.machine.machineType);
  }

  public isMachineOfTypeWeavingMachine(): boolean {
    return this.machine.machineType === MachineType.WEAVING_MACHINE;
  }

  public isMachineOfTypeTuftingMachine(): boolean {
    return this.machine.machineType === MachineType.TUFT;
  }

  public hasMessages(): boolean {
    return !isEmpty(this.machine.messages);
  }

  public onVirtualScrollerEndEvent(event: IPageInfo): void {
    const isAtEndOfList = event.endIndex === size(this.messagesGroups) - 1;
    const mightHaveMoreMessages = this.messages.length % this.SLICE_LENGTH === 0;

    if (isAtEndOfList && mightHaveMoreMessages && this.hasMoreMessages) {
      this.getMessages(size(this.messages));
      this.groupMessagesByChronologicalOrderAndType();
    }
  }

  public createProductionOrderForMachine(): void {
    let url: string;
    if (this.isMachineOfTypeCarpetLoom()) {
      url = RouteUtils.paths.texFab.productionOrder.addProductionOrderForMachine.absolutePath;
    } else if (this.isMachineOfTypeWeavingMachine()) {
      url = RouteUtils.paths.texFab.weaving.addProductionOrderForMachine.absolutePath;
    } else if (this.isMachineOfTypeTuftingMachine()) {
      url = RouteUtils.paths.texFab.tufting.addProductionOrderForMachine.absolutePath;
    } else {
      return;
    }
    this.router.navigate([url.replace(':id', this.machine.id.toString())]);
  }

  public canShowCreateProductionOrderForMachineButton(): boolean {
    let permission: Permission;
    if (this.isMachineOfTypeCarpetLoom()) {
      return true;
    } else if (this.isMachineOfTypeWeavingMachine()) {
      permission = Permission.TEXFAB_WEAVE_QUEUE;
    } else if (this.isMachineOfTypeTuftingMachine()) {
      permission = Permission.TEXFAB_TUFT_QUEUE;
    } else {
      return false;
    }

    return this.currentSubscription?.hasPermission(permission);
  }

  private getMessages(startIndex: number): void {
    const messages = slice(this.machine.messages, startIndex, startIndex + this.SLICE_LENGTH);
    this.hasMoreMessages = !isEmpty(messages);
    this.messages = startIndex === 0 ? messages : concat(this.messages, messages);
  }

  private groupMessagesByChronologicalOrderAndType(): void {
    this.messagesGroups = [];

    if (!isEmpty(this.messages)) {
      let groupOfMessages: MachineMessagesGroup = {messageType: first(this.messages).type, messages: []};

      each(this.messages, (message: Message) => {
        if (groupOfMessages.messageType !== message.type) {
          this.messagesGroups.push(groupOfMessages);

          groupOfMessages = {messageType: message.type, messages: [message]};
        } else {
          groupOfMessages.messages.push(message);
        }
      });

      this.messagesGroups.push(groupOfMessages);
    }
  }

  private getMachineDetails(): void {
    this.planning
      .getMachineDetails(this.machine.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((machineDetails: MachineDetails) => {
        this.machine.activeCreel = machineDetails.creel;
        this.machine.activeQuality = machineDetails.machineQuality;
        this.machine.activeWeaveProduct = machineDetails.weaveProduct;
        this.machine.activeTuftProduct = machineDetails.tuftProduct;
      });
  }
}
